import * as React from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import { CardContent, CardHeader, Typography } from "@mui/material";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import { JobRequest, JobRequestFile, JobRequestFileCategory } from "gen/clients";
import { FileList } from "../../components/FileList/FileList";

interface Props {
  jobRequest: JobRequest;
}

interface FilesByLanguage {
  languageId: string | number;
  languageName: string;
  files: JobRequestFile[];
}

const AIToolboxFilesSection: React.FC<Props> = ({ jobRequest }) => {
  // Group source and translated files by language
  const inputFilesByLanguage = React.useMemo(() => {
    const languageMap = new Map<string | number, FilesByLanguage>();

    // Add source files under source language
    const sourceFiles = jobRequest.files?.filter(f => f.category === JobRequestFileCategory.SOURCE_DOCUMENT) || [];
    if (sourceFiles.length > 0 && jobRequest.sourceLanguage) {
      languageMap.set(jobRequest.sourceLanguage.id, {
        languageId: jobRequest.sourceLanguage.id,
        languageName: jobRequest.sourceLanguage.name,
        files: sourceFiles
      });
    }

    // Add translated files under their respective languages
    const translatedFiles =
      jobRequest.files?.filter(f => f.category === JobRequestFileCategory.TRANSLATED_DOCUMENT) || [];
    translatedFiles.forEach(file => {
      if (file.languageId) {
        const existing = languageMap.get(file.languageId);
        if (existing) {
          existing.files.push(file);
        } else {
          // Find language name from target languages
          const targetLang = jobRequest.targetLanguages?.find(l => l.id === file.languageId);
          const langName = targetLang?.name || `Language ${file.languageId}`;
          languageMap.set(file.languageId, {
            languageId: file.languageId,
            languageName: langName,
            files: [file]
          });
        }
      }
    });

    return Array.from(languageMap.values()).sort((a, b) => a.languageName.localeCompare(b.languageName));
  }, [jobRequest.files, jobRequest.sourceLanguage, jobRequest.targetLanguages]);

  // Get AI_POST_EDITED files for deliverables
  const deliverableFiles = React.useMemo(
    () => jobRequest.files?.filter(f => f.category === JobRequestFileCategory.AI_POST_EDITED) || [],
    [jobRequest.files]
  );

  // Get the target language name for deliverable files header
  const deliverableLanguageName = React.useMemo(() => {
    if (jobRequest.targetLanguages && jobRequest.targetLanguages.length > 0) {
      return jobRequest.targetLanguages[0].name;
    }
    return "";
  }, [jobRequest.targetLanguages]);

  return (
    <Grid container={true} spacing={2}>
      <Grid item={true} xs={12} sm={6}>
        <Card sx={{ mt: 2, minHeight: 300 }}>
          <CardHeader title={<Typography variant="h6">Input Files</Typography>} />
          <CardContent sx={{ overflowY: "auto", pt: 0 }}>
            {inputFilesByLanguage.length === 0 && <Typography variant="body2">No files</Typography>}
            {inputFilesByLanguage.map((group, index) => (
              <Stack key={group.languageId} spacing={1} mt={index > 0 ? 2 : 0}>
                <Typography fontWeight="bold">{group.languageName}</Typography>
                <FileList jobRequestId={jobRequest.id} files={group.files} />
              </Stack>
            ))}
          </CardContent>
        </Card>
      </Grid>
      <Grid item={true} xs={12} sm={6}>
        <Card sx={{ mt: 2, minHeight: 300 }}>
          <CardHeader
            title={
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="h6">Deliverable Files</Typography>
                <Box sx={{ height: 40, paddingTop: "5px" }}>
                  <Typography variant="body1">{deliverableLanguageName}</Typography>
                </Box>
              </Stack>
            }
          />
          <CardContent sx={{ overflowY: "auto", pt: 0 }}>
            {deliverableFiles.length === 0 && <Typography variant="body2">No files</Typography>}
            {deliverableFiles.length > 0 && <FileList jobRequestId={jobRequest.id} files={deliverableFiles} />}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export { AIToolboxFilesSection };
